import { z } from 'zod';

import { STORED_ASSET_NAME_MAX_LENGTH, STORED_ASSET_NAME_RE } from './tenant';

/**
 * THE COLUMNS THAT NAME AN OBJECT IN OUR STORAGE, constrained at the edge.
 *
 * `claimContentAsset` is the chokepoint that proves an uploaded object exists
 * and moves it to the owner's prefix — and it SKIPS any value that is not a
 * bare filename rather than rejecting it, because `mediaSource` legitimately
 * holds a YouTube URL when `mediaType` is `video/youtube`. Its own docblock
 * says what that costs: the eight content DTOs declared these columns as bare
 * `z.string()`, so `my photo.png` — a space is enough — took the skip and was
 * written to the row with no object behind it, under a 201. `contentAssetKey`
 * is bare interpolation (`<tenantId>/<ownerId>/<kind>/<fileName>`), so the
 * value is then concatenated into an S3 key at every read site, and `../`
 * addresses an object outside the prefix it names.
 *
 * The skip is the right behaviour for a URL and the wrong behaviour for a
 * typo, and the DTO is the only place that can tell them apart — which is
 * exactly what that docblock defers to "a DTO change with its own blast
 * radius". This is that change.
 *
 * The EMPTY STRING stays legal, deliberately. Legacy full-form saves use it
 * while an optional cover is absent or unresolved, and
 * `claimChangedContentAsset` already treats it as "nothing to claim". Update
 * routes therefore reserve `null` for explicit removal and treat `''` as a
 * non-destructive no-op.
 */
const isStoredName = (value: string) =>
  value.length > 0 &&
  value.length <= STORED_ASSET_NAME_MAX_LENGTH &&
  STORED_ASSET_NAME_RE.test(value);

/**
 * An `http(s)` URL and nothing else — no `javascript:`, no `data:`, no
 * protocol-relative `//host`. The column is handed back to the browser as a
 * playable source, so a scheme that executes is a stored XSS with a long tail.
 *
 * THIS PREDICATE IS ALSO THE SKIP CONDITION. `claimContentAsset` waives the
 * upload proof for whatever it admits, so a value that gets through is stored
 * with no object behind it and served to viewers — which makes both directions
 * of a mistake expensive, and a hand-rolled `^https:\/\/[^\s]+$` was wrong in
 * both at once:
 *
 * - it REJECTED `HTTPS://…`, a perfectly legal URL, so a legacy row spelled
 *   that way 422'd on every save for the rest of its life;
 * - it ACCEPTED `https://www.youtube.com@attacker.example/evil.mp4`, which reads
 *   as YouTube to a person and resolves to the attacker in a browser, and
 *   accepted C0 control characters, which are not `\s`.
 *
 * Parsed rather than pattern-matched, so the host is whatever the browser will
 * think it is — and `shapeOf` in `content-asset-uploads.ts`, which describes
 * the same value for the log, uses the same case-insensitive scheme test.
 */
export const isExternalMediaUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(value)) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    // userinfo is the whole trick — reject it rather than try to read past it
    return !url.username && !url.password;
  } catch {
    return false;
  }
};

export const isContentMediaSource = (value: unknown): value is string =>
  typeof value === 'string' &&
  (value === '' || isStoredName(value) || isExternalMediaUrl(value));

/** A cover is ALWAYS an object we stored — it has no external form. */
export const CoverSourceName = z
  .string()
  .max(STORED_ASSET_NAME_MAX_LENGTH)
  .refine(
    (value) => value === '' || isStoredName(value),
    'VALIDATION__InvalidAssetName',
  );

/**
 * A media source is either an object we stored or an external video URL. The
 * length bound is larger than a filename's because a YouTube URL with query
 * parameters routinely exceeds 128 characters.
 */
export const MediaSourceValue = z
  .string()
  .max(2048)
  .refine(
    (value) => value === '' || isStoredName(value) || isExternalMediaUrl(value),
    'VALIDATION__InvalidAssetName',
  );
